<div class="h-full w-full bg-base-100 flex flex-col overflow-hidden">
  <!-- Top Bar -->
  <header
    class="bg-base-200/90 backdrop-blur border-b border-base-300 px-4 lg:px-6 h-12 flex items-center justify-between z-10 shrink-0"
  >
    <div class="flex items-center gap-3 min-w-0">
      <a
        routerLink="/dashboard/my-courses"
        class="daisy-btn daisy-btn-ghost daisy-btn-sm text-base-content/80 hover:text-base-content shrink-0"
      >
        <i class="fa-solid fa-arrow-left mr-1.5 text-xs"></i>
        <span class="hidden sm:inline">My Courses</span>
      </a>
      <div class="h-4 w-px bg-base-300 shrink-0"></div>
      <div class="min-w-0">
        <h1 class="text-sm sm:text-base font-bold text-base-content truncate">
          {{ selectedCourse?.title }}
        </h1>
      </div>
    </div>

    <!-- Right Controls -->
    <div class="flex items-center gap-2 shrink-0">
      @if (allFlatResources.length > 0) {
        <span
          class="text-xs text-base-content/60 hidden md:inline px-2.5 py-1 rounded-md bg-base-300/50 font-medium"
        >
          Lesson {{ currentIndex + 1 }} of {{ allFlatResources.length }}
        </span>
      }

      <button
        type="button"
        (click)="toggleSidebar()"
        class="daisy-btn daisy-btn-ghost daisy-btn-sm text-base-content/80 gap-1.5 lg:hidden"
        [title]="isSidebarOpen ? 'Hide Curriculum' : 'Show Curriculum'"
      >
        <i class="fa-solid fa-list-ul text-xs"></i>
        <span class="text-xs">Lessons</span>
      </button>
    </div>
  </header>

  <!-- Grace Period Notification Banner -->
  @if (isInGracePeriod && !isSuspendedOverdue && !isBannerDismissed) {
    <aside
      class="daisy-alert daisy-alert-warning rounded-none py-2 px-4 shadow-sm border-x-0 border-t-0 flex items-center justify-between gap-3 text-xs sm:text-sm shrink-0 z-20"
      aria-label="Payment reminder"
    >
      <div class="flex items-center gap-2.5 min-w-0">
        <i class="fa-solid fa-triangle-exclamation shrink-0 text-sm"></i>
        <span class="truncate sm:whitespace-normal">
          <strong class="font-bold">Installment #{{ pendingOrder?.installmentNumber || 2 }}</strong>
          @if (pendingOrder?.amount) {
            (LKR {{ pendingOrder?.amount?.toLocaleString('en-LK') }})
          }
          due — <span class="font-bold">{{ graceDaysRemaining }} day{{ graceDaysRemaining === 1 ? '' : 's' }}</span> left in grace period.
        </span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <button
          type="button"
          (click)="payInstallment()"
          class="daisy-btn daisy-btn-xs sm:daisy-btn-sm font-semibold gap-1 daisy-btn-warning"
        >
          <i class="fa-solid fa-credit-card text-xs"></i>
          <span>Pay Now</span>
        </button>
        <button
          type="button"
          (click)="dismissBanner()"
          class="daisy-btn daisy-btn-ghost daisy-btn-xs daisy-btn-circle"
          title="Dismiss notification"
          aria-label="Dismiss notification"
        >
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>
    </aside>
  }

  <!-- Pending Approval Notification Banner -->
  @if (isPendingApproval && !isSuspendedOverdue && !isBannerDismissed) {
    <aside
      class="daisy-alert daisy-alert-info rounded-none py-2 px-4 shadow-sm border-x-0 border-t-0 flex items-center justify-between gap-3 text-xs sm:text-sm shrink-0 z-20"
      aria-label="Payment verification notice"
    >
      <div class="flex items-center gap-2.5 min-w-0">
        <i class="fa-solid fa-circle-info shrink-0 text-sm"></i>
        <span class="truncate sm:whitespace-normal">
          <strong class="font-bold">Installment #{{ pendingOrder?.installmentNumber || 2 }}</strong> slip submitted — verification in progress.
        </span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <button
          type="button"
          (click)="viewOrders()"
          class="daisy-btn daisy-btn-xs sm:daisy-btn-sm font-semibold gap-1 daisy-btn-info text-white"
        >
          <i class="fa-solid fa-receipt text-xs"></i>
          <span>View Orders</span>
        </button>
        <button
          type="button"
          (click)="dismissBanner()"
          class="daisy-btn daisy-btn-ghost daisy-btn-xs daisy-btn-circle"
          title="Dismiss notification"
          aria-label="Dismiss notification"
        >
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>
    </aside>
  }

  <!-- Main Player & Playlist Grid -->
  <div
    class="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden relative"
  >
    <!-- Video Player Area -->
    <main
      class="flex-1 flex flex-col bg-neutral-950 min-h-0 overflow-hidden transition-all duration-200 relative"
      [class.w-full]="!isSidebarOpen"
    >
      <!-- Collapsed Sidebar Expand Tab (Desktop - Mid height) -->
      @if (!isSidebarOpen) {
        <button
          type="button"
          (click)="toggleSidebar()"
          class="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-base-200/90 hover:bg-base-300 border border-r-0 border-base-300 py-3.5 px-1.5 rounded-l-md shadow-xl flex-col items-center gap-2 text-base-content/70 hover:text-base-content transition-all cursor-pointer group backdrop-blur"
          title="Open Course Content"
        >
          <i
            class="fa-solid fa-chevron-left text-xs group-hover:-translate-x-0.5 transition-transform"
          ></i>
          <span
            class="text-[10px] font-semibold tracking-wider [writing-mode:vertical-lr] rotate-180 select-none"
            >Course Content</span
          >
        </button>
      }

      <!-- Video Container Stage -->
      <div
        class="flex-1 min-h-0 w-full flex items-center justify-center bg-black relative p-0 lg:p-3 overflow-hidden"
      >
        @if (isSuspendedOverdue) {
          <div
            class="w-full aspect-video max-h-full max-w-full bg-neutral-900/95 relative flex flex-col items-center justify-center p-6 text-center shadow-2xl rounded-none lg:rounded-xl ring-1 ring-error/30"
          >
            <div
              class="w-16 h-16 rounded-full bg-error/10 text-error flex items-center justify-center mb-4"
            >
              <i class="fa-solid fa-lock text-2xl"></i>
            </div>
            <h3 class="text-lg sm:text-xl font-bold text-white mb-2">
              Course Access Suspended
            </h3>
            <p class="text-sm text-neutral-300 max-w-md mb-6 leading-relaxed">
              Your installment payment
              @if (pendingOrder?.amount) {
                <span>of <strong class="text-white">LKR {{ pendingOrder?.amount?.toLocaleString('en-LK') }}</strong></span>
              }
              is overdue and the grace period has ended. Please complete payment to immediately resume your access.
            </p>
            <button
              type="button"
              (click)="payInstallment()"
              class="daisy-btn daisy-btn-error gap-2 text-white font-semibold shadow-lg"
            >
              <i class="fa-solid fa-credit-card"></i>
              <span>Pay Now to Restore Access</span>
            </button>
          </div>
        } @else {
          <div
            class="w-full aspect-video max-h-full max-w-full bg-black relative flex items-center justify-center overflow-hidden shadow-2xl rounded-none lg:rounded-xl ring-1 ring-white/10"
          >
            <router-outlet></router-outlet>
          </div>
        }
      </div>

      <!-- Lesson Info & Controls Below Video -->
      <div
        class="w-full bg-base-200/80 border-t border-base-300 px-4 sm:px-6 lg:px-8 py-3 shrink-0 z-10"
      >
        <div
          class="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        >
          <!-- Active Lesson Details -->
          <div class="min-w-0 space-y-0.5">
            <div class="flex items-center gap-2 flex-wrap">
              @if (activeSectionTopic) {
                <span
                  class="daisy-badge daisy-badge-primary daisy-badge-sm font-medium"
                >
                  {{ activeSectionTopic }}
                </span>
              }
              @if (currentIndex >= 0) {
                <span class="text-xs text-base-content/60">
                  Lesson {{ currentIndex + 1 }}
                </span>
              }
            </div>
            <h2 class="text-base sm:text-lg font-bold text-base-content truncate">
              {{ activeLessonTitle || 'Select a lesson to begin' }}
            </h2>
          </div>

          <!-- Prev / Next Navigation Controls -->
          <div class="flex items-center gap-2 shrink-0">
            <button
              type="button"
              class="daisy-btn daisy-btn-sm daisy-btn-outline border-base-300 hover:bg-base-300 disabled:opacity-30 text-base-content"
              [disabled]="!hasPrev || isSuspendedOverdue"
              (click)="goToPrev()"
            >
              <i class="fa-solid fa-chevron-left text-xs mr-1"></i> Previous
            </button>
            <button
              type="button"
              class="daisy-btn daisy-btn-sm daisy-btn-primary disabled:opacity-30"
              [disabled]="!hasNext || isSuspendedOverdue"
              (click)="goToNext()"
            >
              Next <i class="fa-solid fa-chevron-right text-xs ml-1"></i>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Mobile Drawer Overlay Backdrop -->
    @if (isSidebarOpen) {
      <div
        class="fixed inset-0 bg-black/60 z-30 lg:hidden backdrop-blur-sm transition-opacity"
        (click)="toggleSidebar()"
      ></div>
    }

    <!-- Curriculum Playlist Sidebar -->
    <aside
      class="fixed inset-y-0 right-0 z-40 w-5/6 sm:w-80 lg:static lg:z-auto lg:w-80 xl:w-96 bg-base-100 border-l border-base-300 flex flex-col shrink-0 h-full overflow-hidden transition-all duration-200"
      [class.hidden]="!isSidebarOpen"
    >
      <!-- Open Sidebar Collapse Tab (Desktop - Mid height) -->
      <button
        type="button"
        (click)="toggleSidebar()"
        class="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-base-200/90 hover:bg-base-300 border border-l-0 border-base-300 py-3.5 px-1.5 rounded-r-md shadow-xl flex-col items-center gap-2 text-base-content/70 hover:text-base-content transition-all cursor-pointer group backdrop-blur"
        title="Collapse Course Content"
      >
        <i
          class="fa-solid fa-chevron-right text-xs group-hover:translate-x-0.5 transition-transform"
        ></i>
        <span
          class="text-[10px] font-semibold tracking-wider [writing-mode:vertical-lr] rotate-180 select-none"
          >Course Content</span
        >
      </button>

      <!-- Sidebar Header -->
      <div
        class="p-3.5 border-b border-base-300 bg-base-200/50 flex items-center justify-between shrink-0"
      >
        <div>
          <h2 class="font-bold text-base-content text-sm sm:text-base">
            Course Content
          </h2>
          <p class="text-xs text-base-content/60 mt-0.5">
            {{ selectedCourse?.contents?.length || 0 }} sections •
            {{ allFlatResources.length }} lessons
          </p>
        </div>
        <!-- Mobile-only close button -->
        <button
          type="button"
          (click)="toggleSidebar()"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square text-base-content/70 hover:text-base-content lg:hidden"
          title="Collapse Course Content"
        >
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>

      <!-- Section Accordion List with DaisyUI Collapse -->
      <div class="flex-1 overflow-y-auto divide-y divide-base-300">
        @for (
          content of selectedCourse?.contents;
          track content;
          let sIdx = $index
        ) {
          <div
            class="daisy-collapse daisy-collapse-arrow bg-base-100 rounded-none"
          >
            <input
              type="checkbox"
              [checked]="activeSectionIndex === sIdx"
              (change)="toggleSection(sIdx)"
            />
            <div
              class="daisy-collapse-title flex items-center justify-between p-3.5 cursor-pointer hover:bg-base-200/60 transition-colors select-none font-semibold text-xs sm:text-sm text-base-content pr-10"
            >
              <div class="flex items-center gap-2 truncate min-w-0">
                <span
                  class="w-5 h-5 rounded bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0"
                >
                  {{ sIdx + 1 }}
                </span>
                <span class="truncate">{{ content.topic }}</span>
              </div>
              <span
                class="text-[11px] text-base-content/50 font-normal shrink-0"
              >
                {{ content.resources ? content.resources.length : 0 }}
              </span>
            </div>

            <div class="daisy-collapse-content p-0">
              <div class="divide-y divide-base-200 bg-base-200/20">
                @for (resource of content.resources; track resource) {
                  <button
                    type="button"
                    (click)="changeCurrentVideo(resource)"
                    class="w-full flex items-center justify-between p-3 text-left hover:bg-base-200/50 transition-all text-xs group"
                    [class.bg-primary]="resource._id === activeResourceId"
                    [class.text-primary-content]="
                      resource._id === activeResourceId
                    "
                    [class.font-semibold]="resource._id === activeResourceId"
                  >
                    <div class="flex items-center gap-2.5 truncate min-w-0">
                      @if (getResourceType(resource) === 'document') {
                        <i
                          class="fa-solid fa-file-pdf text-xs shrink-0"
                          [class.text-error]="resource._id !== activeResourceId"
                          [class.text-primary-content]="
                            resource._id === activeResourceId
                          "
                        ></i>
                      } @else if (getResourceType(resource) === 'link') {
                        <i
                          class="fa-solid fa-arrow-up-right-from-square text-xs shrink-0"
                          [class.text-info]="resource._id !== activeResourceId"
                          [class.text-primary-content]="
                            resource._id === activeResourceId
                          "
                        ></i>
                      } @else {
                        <i
                          class="fa-solid text-xs shrink-0"
                          [class.fa-circle-play]="
                            resource._id !== activeResourceId
                          "
                          [class.fa-circle-pause]="
                            resource._id === activeResourceId
                          "
                          [class.text-primary]="
                            resource._id !== activeResourceId
                          "
                        ></i>
                      }
                      <span class="truncate">{{ resource.title }}</span>
                    </div>

                    <div class="flex items-center gap-1.5 shrink-0 ml-2">
                      @if (resource.isFreePreview) {
                        <span
                          class="daisy-badge daisy-badge-xs shrink-0"
                          [class.daisy-badge-neutral]="
                            resource._id === activeResourceId
                          "
                          [class.daisy-badge-ghost]="
                            resource._id !== activeResourceId
                          "
                        >
                          Preview
                        </span>
                      }
                    </div>
                  </button>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </aside>
  </div>
</div>
